import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addDeliveryRange } from '../store/slices/pricingSlice';
import { addToast } from '../store/slices/toastsSlice';
import { v4 as uuidv4 } from 'uuid';

interface CreateRangeModalProps {
  onClose: () => void;
}

const CreateRangeModal: React.FC<CreateRangeModalProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const [minDistance, setMinDistance] = useState('');
  const [maxDistance, setMaxDistance] = useState('');
  const [deliveryPrice, setDeliveryPrice] = useState('');

  const handleMinDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, digits, and one decimal point with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setMinDistance(value);
    }
  };

  const handleMaxDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, digits, and one decimal point with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setMaxDistance(value);
    }
  };

  const handleDeliveryPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, digits, and one decimal point with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setDeliveryPrice(value);
    }
  };

  const handleSave = () => {
    const parsedMinDistance = parseFloat(minDistance);
    const parsedMaxDistance = parseFloat(maxDistance);
    const parsedPrice = parseFloat(deliveryPrice);

    if (!minDistance || !maxDistance || !deliveryPrice) {
      dispatch(addToast('Please fill in all fields', 'error'));
      return;
    }

    if (isNaN(parsedMinDistance) || parsedMinDistance < 0) {
      dispatch(addToast('Please enter a valid minimum distance', 'error'));
      return;
    }

    if (isNaN(parsedMaxDistance) || parsedMaxDistance < 0) {
      dispatch(addToast('Please enter a valid maximum distance', 'error'));
      return;
    }

    if (parsedMinDistance >= parsedMaxDistance) {
      dispatch(addToast('Maximum distance must be greater than minimum distance', 'error'));
      return;
    }

    if (isNaN(parsedPrice) || parsedPrice < 0) {
      dispatch(addToast('Please enter a valid delivery price', 'error'));
      return;
    }

    const newRange = {
      id: uuidv4(),
      minDistance: parsedMinDistance,
      maxDistance: parsedMaxDistance,
      deliveryPrice: parsedPrice
    };

    dispatch(addDeliveryRange(newRange));
    dispatch(addToast('Delivery range created successfully!', 'success'));
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Create New Range</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.formGroup}>
            <label htmlFor="minDistance" style={styles.label}>
              Min Distance (km):
            </label>
            <input
              id="minDistance"
              type="text"
              value={minDistance}
              onChange={handleMinDistanceChange}
              onKeyDown={handleKeyDown}
              style={styles.input}
              placeholder="e.g., 0"
              autoFocus
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="maxDistance" style={styles.label}>
              Max Distance (km):
            </label>
            <input
              id="maxDistance"
              type="text"
              value={maxDistance}
              onChange={handleMaxDistanceChange}
              onKeyDown={handleKeyDown}
              style={styles.input}
              placeholder="e.g., 10"
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="deliveryPrice" style={styles.label}>
              Delivery Price:
            </label>
            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <span style={styles.dollarSign}>$</span>
              <input
                id="deliveryPrice"
                type="text"
                value={deliveryPrice}
                onChange={handleDeliveryPriceChange}
                onKeyDown={handleKeyDown}
                style={{ ...styles.input, paddingLeft: '28px' }}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button style={styles.saveButton} onClick={handleSave}>
            Save Range
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    padding: '30px 20px'
  },
  formGroup: {
    marginBottom: '25px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  dollarSign: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#666',
    fontSize: '16px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default CreateRangeModal;
